import { load as loadRecaptchaV3 } from 'recaptcha-v3';

const RECAPTCHA_V3_SITE_KEY = '6LeTCUEqAAAAANgTkhj9HeAv2KtCUjJkPc9QDAMh';
const RECAPTCHA_V2_SITE_KEY = '6Lf-gG0rAAAAAEqg0_0b-90DE3jGsg13i__ZQ6gy';

// Forms that require v2 (all others use the global v3)
const V2_FORMS = [
  'sweeps',
  'advisor_request',
  'advisor_short_request',
  'dpa_request',
  'dpa_distributor_request',
];

// Global instances - only load each version once
let globalV3Instance = null;
let globalV2LoadPromise = null;

class RecaptchaV2Wrapper {
  constructor(siteKey, formElement) {
    this.siteKey = siteKey;
    this.formElement = formElement;
    this.widgetId = null;
    this.isReady = false;
    this.version = 'v2';
    this.responseToken = null;
    this.autoRender = true;
    this.completionCallback = null;
  }

  init() {
    return new Promise((resolve, reject) => {
      if (!window.grecaptcha) {
        reject(new Error('reCAPTCHA v2 not ready'));
        return;
      }

      this.renderWidget()
        .then(() => {
          this.isReady = true;
          resolve();
        })
        .catch(reject);
    });
  }

  execute(action = 'submit') {
    return new Promise((resolve, reject) => {
      console.log('v2 execute called');

      if (!this.isReady || !window.grecaptcha) {
        reject(new Error('reCAPTCHA v2 not ready'));
        return;
      }

      if (this.responseToken) {
        console.log('v2 already completed');
        resolve(this.responseToken);
        return;
      }

      reject(new Error('Please complete the reCAPTCHA verification by clicking the checkbox.'));
    });
  }

  renderWidget() {
    return new Promise((resolve, reject) => {
      try {
        console.log('Rendering visible v2 widget in form');

        let container = this.formElement.querySelector('.recaptcha-container, .recaptcha-placeholder, [data-recaptcha-container]');

        if (!container) {
          container = document.createElement('div');
          container.className = 'recaptcha-container';
          container.style.margin = '20px 0';

          const submitBtn = this.formElement.querySelector('[type="submit"]');
          if (submitBtn) {
            submitBtn.parentNode.insertBefore(container, submitBtn);
          } else {
            this.formElement.appendChild(container);
          }

          console.log('Created v2 container in form');
        } else {
          console.log('Using existing reCAPTCHA placeholder');
        }

        this.widgetId = window.grecaptcha.render(container, {
          sitekey: this.siteKey,
          size: 'normal',
          theme: 'light',
          callback: (token) => {
            console.log('v2 user completed reCAPTCHA');
            this.responseToken = token;
            // Call the completion callback if set
            if (this.completionCallback) {
              this.completionCallback(token);
            }
          },
          'error-callback': () => {
            console.error('reCAPTCHA v2 error');
            this.responseToken = null;
          },
          'expired-callback': () => {
            console.warn('reCAPTCHA v2 expired');
            this.responseToken = null;
          }
        });

        console.log('v2 widget rendered with ID:', this.widgetId);
        resolve();

      } catch (error) {
        console.error('Error rendering v2 widget:', error);
        reject(error);
      }
    });
  }

  reset() {
    try {
      if (this.widgetId !== null && window.grecaptcha && window.grecaptcha.reset) {
        window.grecaptcha.reset(this.widgetId);
        this.responseToken = null;
        console.log('v2 widget reset');
      }
    } catch (error) {
      console.error('Error resetting v2 widget:', error);
    }
  }

  isCompleted() {
    return !!this.responseToken;
  }

  // New method to set completion callback
  setCompletionCallback(callback) {
    this.completionCallback = callback;
  }
}

//Ensure v2 script is only loaded once
function ensureRecaptchaV2Loaded() {
  // Return existing promise if already loading/loaded
  if (globalV2LoadPromise) {
    return globalV2LoadPromise;
  }

  // Check if already loaded
  if (window.grecaptcha && window.grecaptcha.render) {
    console.log('reCAPTCHA v2 script already loaded');
    globalV2LoadPromise = Promise.resolve();
    return globalV2LoadPromise;
  }

  // Create single loading promise
  globalV2LoadPromise = new Promise((resolve, reject) => {
    console.log('Loading reCAPTCHA v2 script');

    const callbackName = 'onRecaptchaV2Load' + Date.now();

    window[callbackName] = () => {
      console.log('v2 script loaded');
      resolve();
      delete window[callbackName];
    };

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?onload=${callbackName}&render=explicit`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      delete window[callbackName];
      globalV2LoadPromise = null; // Reset so it can be retried
      reject(new Error('Failed to load reCAPTCHA v2 script'));
    };

    document.head.appendChild(script);
  });

  return globalV2LoadPromise;
}

//Use the single script loading promise
function loadRecaptchaV2(siteKey, formElement) {
  return ensureRecaptchaV2Loaded()
    .then(() => {
      const recaptcha = new RecaptchaV2Wrapper(siteKey, formElement);
      return recaptcha.init()
        .then(() => recaptcha);
    });
}

// Initialize v3 globally
export function initGlobalV3() {
  if (!globalV3Instance) {
    console.log('Initializing global reCAPTCHA v3');
    globalV3Instance = loadRecaptchaV3(RECAPTCHA_V3_SITE_KEY)
      .then(recaptcha => {
        recaptcha.version = 'v3';
        console.log('Global v3 instance created');
        return recaptcha;
      })
      .catch(error => {
        console.error('Error loading v3:', error);
        globalV3Instance = null;
        throw error;
      });
  }
  return globalV3Instance;
}

// Main loader function
export function loadRecaptcha(formHandle, formElement = null) {
  const needsV2 = V2_FORMS.includes(formHandle);

  if (needsV2) {
    console.log(`Loading reCAPTCHA v2 for ${formHandle} form`);
    return loadRecaptchaV2(RECAPTCHA_V2_SITE_KEY, formElement)
      .then(recaptcha => {
        recaptcha.version = 'v2';
        return recaptcha;
      });
  } else {
    console.log(`Using global reCAPTCHA v3 for ${formHandle} form`);
    return initGlobalV3();
  }
}

export function autoInit() {
  return initGlobalV3();
}

export { loadRecaptchaV3, loadRecaptchaV2 };
export default {
  loadRecaptcha,
  initGlobalV3,
  autoInit
};
